import {
  Checkbox,
  FormControlLabel,
  Icon,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { useAuth } from '@schooly/components/authentication';
import { DropdownAttendanceCodes, SearchInput, UserAvatar } from '@schooly/components/filters';
import { ATTENDANCE, buildTestId } from '@schooly/constants/test-ids';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CODE_CIRCLE_SIZE,
  TakeAttendanceEntry,
  TakeAttendanceListRow,
  useTakeAttendanceModal,
} from './TakeAttendanceModalContext';
import { getUserTypeTextId } from '../../../helpers/misc';
import {
  ArrowDownV2Icon,
  BellIcon,
  ChevronDownSmallIcon,
  DropdownComments,
  PlusIcon,
  SimpleButton,
  theme,
  TypographyWithOverflowHint,
  VirtualizedList,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { JustificationStatus } from '@schooly/api';
import { AttendanceGridTrackingPoint } from '../../Attendance/AttendanceTable/common/types';
import { CellIndicator } from '../../Attendance/AttendanceTable/common/AttendanceCells';
import {
  TAKE_ATTENDANCE_COMMENT_CELL_CLASS,
  TakeAttendanceCommentCell,
} from './TakeAttendanceCommentCell';
import { JustificationInfo } from '@schooly/components/justifications';
import { getResultingJustification } from '../../Attendance/attendance-utils';
import {
  CodeChangeEntry,
  useJustificationCodeChange,
} from '../../Attendance/useJustificationCodeChange';
import { pickSessionEntries, pickTargetSessions } from './take-attendance-utils';
import { AbsenceNoticeInfo } from '../../../components/common/AbsenceNoticeInfo/AbsenceNoticeInfo';
import {
  AttendanceStatsBadge,
  mapTakeAttendanceListRowToAttendanceStats,
} from './AttendanceStatsBadge';

export const TakeAttendanceModalContent: FC = () => {
  const { formatMessage } = useIntl();
  const { query, setQuery, rows, trackingPoints, activePoint } = useTakeAttendanceModal();

  // When periods are shown, only the active column is rendered in full; the rest
  // collapse to a compact code circle.
  const hasPeriods = useMemo(() => trackingPoints.some((point) => point.period), [trackingPoints]);

  const filteredRows = useMemo(() => {
    if (!query) return rows;

    return rows.filter((r) =>
      getUserFullName(r.student.user).toLowerCase().includes(query.toLowerCase()),
    );
  }, [rows, query]);

  const renderHeaderCell = (point: AttendanceGridTrackingPoint) => {
    const isActive = point.key === activePoint?.key;
    const hasActiveBorder = isActive && trackingPoints.length > 1;
    const compact = hasPeriods && !isActive;

    return (
      <Stack
        key={point.key}
        sx={{
          ...columnWidthSx(isActive, compact),
          height: ROW_HEIGHT,
          px: 1,
          gap: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: compact ? 'center' : 'flex-start',
          ...(hasActiveBorder
            ? {
                borderTop: theme.mixins.borderValue(),
                borderLeft: theme.mixins.borderValue(),
                borderRight: theme.mixins.borderValue(),
                borderColor: theme.palette.common.main4,
                borderTopLeftRadius: theme.spacing(1),
                borderTopRightRadius: theme.spacing(1),
              }
            : {}),
        }}
      >
        <Typography color={isActive ? 'primary.main' : 'text.secondary'}>{point.label}</Typography>
        {(!hasPeriods || isActive) && (
          <AttendanceStatsBadge
            computeStats={mapTakeAttendanceListRowToAttendanceStats({ rows, pointKey: point.key })}
          />
        )}
      </Stack>
    );
  };

  return (
    <Stack sx={{ height: '100%', marginBottom: '-1px' }}>
      <SearchInput
        value={query}
        onChangeText={setQuery}
        placeholder={formatMessage(
          { id: 'people-SearchAmongType' },
          {
            userTypePlural: formatMessage({
              id: getUserTypeTextId('student', true),
            }).toLowerCase(),
          },
        )}
      />
      <Stack
        sx={(theme) => ({
          mt: 2,
          height: ROW_HEIGHT,
          flexDirection: 'row',
          alignItems: 'flex-end',
          borderBottom: theme.mixins.borderValue(),
          borderColor: theme.palette.common.main4,
          marginBottom: '-1px',
          position: 'relative',
          zIndex: 1,
        })}
      >
        <Stack width={STUDENT_NAME_CELL_WIDTH}>
          <MarkBlankAsCell />
        </Stack>
        {trackingPoints.map(renderHeaderCell)}
      </Stack>
      {filteredRows.length ? (
        <VirtualizedList
          items={filteredRows}
          renderItem={(i, row) => (
            <TakeAttendanceRow
              isLast={i === filteredRows.length - 1}
              key={row.student.user.relation_id}
              row={row}
            />
          )}
        />
      ) : (
        <Stack width="100%" height="100%" justifyContent="center" alignItems="center">
          <Typography variant="h3" color="text.secondary">
            <FormattedMessage id="noSearchResults" />
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const ROW_HEIGHT = 44;
const STUDENT_NAME_CELL_WIDTH = 240;
const ACTIVE_CELL_MIN_WIDTH = 300;
const COMPACT_CELL_WIDTH = 56;

/** The active column stays wide (min 300px); compact columns are fixed-narrow. */
const columnWidthSx = (isActive: boolean, compact: boolean) => {
  if (isActive) {
    return { flex: 1, minWidth: ACTIVE_CELL_MIN_WIDTH, height: ROW_HEIGHT };
  }

  if (compact) {
    return {
      flex: '0 0 auto',
      width: COMPACT_CELL_WIDTH,
      height: ROW_HEIGHT,
      justifyContent: 'center',
      pl: 0,
      borderLeft: 0,
    };
  }

  return { flex: 1, height: ROW_HEIGHT };
};

const TakeAttendanceRow: FC<{ row: TakeAttendanceListRow; isLast: boolean }> = ({
  row,
  isLast,
}) => {
  const { activePoint, trackingPoints, canViewStudents, codes } = useTakeAttendanceModal();
  const { currentStaff } = useAuth();
  const { user } = row.student;

  const hasPeriods = trackingPoints.some((point) => point.period);
  const absenceComment = row.absence_request?.comment;
  const { justification, justificationNote } = row;

  const sessionEntries = useMemo(() => pickSessionEntries(row.entries), [row.entries]);

  const resultingJustification = getResultingJustification({
    justification,
    note: justificationNote,
    codes,
    entries: sessionEntries,
  });

  const renderFor = (point: AttendanceGridTrackingPoint) => {
    const entry = row.entries[point.key];
    const isActive = point.key === activePoint?.key;
    // Non-active columns collapse to a compact code circle when periods are shown.
    const compact = hasPeriods && !isActive;

    if (!entry) {
      return (
        <TakeAttendanceRowCellContainer
          key={point.key}
          withBorder={!compact && isActive}
          sx={columnWidthSx(!compact && isActive, compact)}
        >
          <NoRegisterCodeWithTooltip>
            {!compact && (
              <Typography>
                <FormattedMessage id="attendance-NoRegister" />
              </Typography>
            )}
          </NoRegisterCodeWithTooltip>
        </TakeAttendanceRowCellContainer>
      );
    }

    return (
      <TakeAttendanceRowCodeCell
        key={point.key}
        editable={isActive}
        compact={compact}
        label={point.label}
        entry={entry}
        justification={justification}
        justificationNote={row.justificationNote}
        relation={user}
        sessionEntries={sessionEntries}
        absenceNotice={absenceComment ?? null}
      >
        {!compact && (isActive || !!entry.text) && (
          <TakeAttendanceCommentCell
            entry={entry}
            justificationNote={row.justificationNote}
            editMode={isActive}
          />
        )}
      </TakeAttendanceRowCodeCell>
    );
  };

  return (
    <Stack
      data-test-id={buildTestId(
        ATTENDANCE.ENTRY_ROW_PREFIX,
        { name: getUserFullName(user) },
        'name',
      )}
      flexDirection="row"
      alignItems="center"
      height={ROW_HEIGHT}
      sx={(theme) => ({
        borderTop: theme.mixins.borderValue(),
        borderBottom: isLast ? theme.mixins.borderValue() : undefined,
        ['.' + TAKE_ATTENDANCE_COMMENT_CELL_CLASS]: {
          opacity: 0,
          transition: 'opacity .25s',
        },
        '&:hover': {
          backgroundColor: theme.palette.common.lightBg,
          '.MuiTypography-root': { color: theme.palette.primary.main },
          ['.' + TAKE_ATTENDANCE_COMMENT_CELL_CLASS]: {
            opacity: 1,
            color: theme.palette.primary.main,
          },
        },
      })}
    >
      <Stack
        flexDirection="row"
        gap={1}
        width={STUDENT_NAME_CELL_WIDTH}
        pl="1px"
        pr={1}
        alignItems="center"
      >
        <UserAvatar user={user} />
        <TypographyWithOverflowHint variant="h3" color="text.primary" flex={1}>
          {getUserFullName(user)}
        </TypographyWithOverflowHint>
        <Stack direction="row" gap={1} alignItems="center">
          {!!absenceComment && <AbsenceNoticeInfo comment={absenceComment} />}
          {resultingJustification && (
            <JustificationInfo
              justification={resultingJustification}
              pendingNote={
                justificationNote && currentStaff
                  ? { ...justificationNote, creator: currentStaff }
                  : undefined
              }
              canViewStudents={canViewStudents}
              relationId={user.relation_id}
              emptyTextIds={{
                [JustificationStatus.Pending]: 'attendance-justification-OnceSaved',
              }}
            />
          )}
        </Stack>
      </Stack>
      {trackingPoints.map(renderFor)}
    </Stack>
  );
};

const TakeAttendanceRowCodeCell: FC<
  PropsWithChildren<{
    label: string;
    entry: TakeAttendanceEntry;
    editable: boolean;
    compact?: boolean;
    justification: TakeAttendanceListRow['justification'];
    justificationNote: TakeAttendanceListRow['justificationNote'];
    relation: TakeAttendanceListRow['student']['user'];
    sessionEntries: CodeChangeEntry[];
    absenceNotice: string | null;
  }>
> = ({
  children,
  entry,
  label,
  editable,
  compact,
  justification,
  justificationNote,
  relation,
  sessionEntries,
  absenceNotice,
}) => {
  const { codeOptions, codes, isoDate, onUpdateAttendanceEntry } = useTakeAttendanceModal();
  const { currentStaff } = useAuth();
  const { confirmCodeChange } = useJustificationCodeChange();
  const selectedOption = codeOptions.flat().find((o) => o.id === entry.attendance_code_id);

  const handleSelect = async (option: { id: string }) => {
    const code = codes.find((item) => item.id === option.id);
    if (!code) return;

    const result = await confirmCodeChange({
      newCode: code,
      codes,
      entries: sessionEntries,
      sessions: pickTargetSessions(entry),
      justification,
      pendingNote: justificationNote,
      absenceNotice,
      relation,
      date: isoDate,
    });
    if (!result) return;

    onUpdateAttendanceEntry({ ...entry, attendance_code_id: option.id }, result.note);
  };

  if (compact) {
    const hasNote = !!entry.text;
    const codeCircle = selectedOption ? (
      <Tooltip title={selectedOption.label}>
        <Stack alignItems="center">{selectedOption.avatar}</Stack>
      </Tooltip>
    ) : (
      <NoStatusCodeWithTooltip sessionLabel={label} />
    );

    return (
      <TakeAttendanceRowCellContainer withBorder={false} sx={columnWidthSx(false, true)}>
        {hasNote && <CellIndicator />}
        {hasNote ? (
          <DropdownComments
            comments={[
              {
                comment: entry.text ?? '',
                relation_id: entry.creator_relation_id ?? '',
                title: entry.creator_title,
                given_name: entry.creator_given_name,
                last_name: entry.creator_last_name ?? '',
                known_as: entry.creator_known_as,
              },
            ]}
            canAdd={false}
            canEditOwn={false}
            canEditOther={false}
            disabled={false}
            tableView
            currentStaff={currentStaff}
            renderIcon={() => (
              <Stack width={COMPACT_CELL_WIDTH} alignItems="center" sx={{ cursor: 'pointer' }}>
                {codeCircle}
              </Stack>
            )}
          />
        ) : (
          codeCircle
        )}
      </TakeAttendanceRowCellContainer>
    );
  }

  const renderSelectedCode = (opened: boolean, option?: typeof selectedOption) => {
    if (!option) {
      if (!editable) {
        return (
          <NoStatusCodeWithTooltip sessionLabel={label}>
            <Typography color="common.grey">
              <FormattedMessage id="attendance-NoStatus" />
            </Typography>
          </NoStatusCodeWithTooltip>
        );
      }

      return (
        <SimpleButton startIcon={<PlusIcon />} size="small" sx={{ gap: 0.5 }}>
          <FormattedMessage id="attendance-AddCodes" />
        </SimpleButton>
      );
    }

    return (
      <>
        {option.avatar}
        <TypographyWithOverflowHint color="inherit">
          {option.label ?? '-'}
        </TypographyWithOverflowHint>
        {!!option.withNotification && (
          <Icon inverse sx={(theme) => ({ fontSize: theme.spacing(1.5) })}>
            <BellIcon />
          </Icon>
        )}
        {editable && (
          <Icon
            className="arrowIcon"
            sx={{
              width: (theme) => theme.spacing(1),
              transform: opened ? 'rotate(180deg)' : undefined,
            }}
          >
            <ArrowDownV2Icon />
          </Icon>
        )}
      </>
    );
  };

  return (
    <TakeAttendanceRowCellContainer withBorder={editable} sx={columnWidthSx(editable, false)}>
      <DropdownAttendanceCodes
        width={325}
        options={codeOptions}
        selectedOption={selectedOption}
        onSelect={handleSelect}
        disabled={!editable}
      >
        {(opened) => (
          <Stack direction="row" gap={1} alignItems="center">
            {renderSelectedCode(opened, selectedOption)}
          </Stack>
        )}
      </DropdownAttendanceCodes>
      {children}
    </TakeAttendanceRowCellContainer>
  );
};

const TakeAttendanceRowCellContainer = styled(Stack)<{
  withBorder: boolean;
}>(({ theme, withBorder }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  position: 'relative',
  justifyContent: 'space-between',
  height: ROW_HEIGHT,
  paddingLeft: `calc(${theme.spacing(1)} - 1px)`,
  flex: 1,
  borderLeft: theme.mixins.borderValue(),
  borderLeftColor: withBorder ? theme.palette.common.main4 : undefined,
  ...(withBorder
    ? {
        borderRight: theme.mixins.borderValue(),
      }
    : {}),
  borderColor: withBorder ? theme.palette.common.main4 : undefined,
}));

const MarkBlankAsCell: FC = () => {
  const { codeOptions, codes, markBlankAs, allMarked } = useTakeAttendanceModal();
  const { formatMessage } = useIntl();
  const [markAsCodeId, setMarkAsCodeId] = useState<string | undefined>(codes[0]?.id);

  useEffect(() => {
    if (!markAsCodeId && codes?.length) {
      setMarkAsCodeId(codes[0].id);
    }
  }, [codes, markAsCodeId]);

  const handleCheckAll = () => {
    if (!markAsCodeId) return;
    markBlankAs(markAsCodeId);
  };
  const selectedOption = codeOptions.flat().find((o) => o.id === markAsCodeId);

  if (!markAsCodeId) return null;

  return (
    <Stack
      direction="row"
      gap={0.5}
      alignItems="center"
      height={ROW_HEIGHT}
      sx={{ pointerEvents: allMarked ? 'none' : undefined }}
    >
      <FormControlLabel
        label={
          <Typography variant="h3" color="text.primary" noWrap>
            {formatMessage({ id: 'attendance-MarkBlankAs' })}
          </Typography>
        }
        control={<Checkbox checked={allMarked} onChange={handleCheckAll} />}
        sx={{ mr: 0, ml: -1 }}
      />
      <DropdownAttendanceCodes
        options={codeOptions}
        selectedOption={selectedOption}
        onSelect={(code) => setMarkAsCodeId(code.id)}
        disabled={allMarked}
      >
        {(opened) => (
          <Stack direction="row" gap={0.5} alignItems="center">
            {selectedOption?.avatar ?? '-'}
            <Icon
              className="arrowIcon"
              sx={{
                width: (theme) => theme.spacing(2),
                transform: opened ? 'rotate(180deg)' : undefined,
              }}
            >
              <ChevronDownSmallIcon />
            </Icon>
          </Stack>
        )}
      </DropdownAttendanceCodes>
    </Stack>
  );
};

const NoRegisterCodeWithTooltip: FC<PropsWithChildren> = ({ children }) => {
  const { formatMessage } = useIntl();

  return (
    <Tooltip title={formatMessage({ id: 'attendance-NoRegisterForPeriodTooltip' })}>
      <Stack direction="row" gap={1} alignItems="center">
        <AttendanceCodeCircle
          size={CODE_CIRCLE_SIZE}
          sx={(theme) => ({ backgroundColor: theme.palette.common.main5, border: 'none' })}
        />
        {children}
      </Stack>
    </Tooltip>
  );
};

const NoStatusCodeWithTooltip: FC<PropsWithChildren & { sessionLabel: string }> = ({
  children,
  sessionLabel,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Tooltip
      title={formatMessage({ id: 'attendance-SessionNotTakenYet' }, { session: sessionLabel })}
    >
      <Stack
        flexDirection="row"
        height={ROW_HEIGHT}
        sx={(theme) => ({
          '&:hover .code': {
            borderColor: theme.palette.warning.main,
          },
        })}
        alignItems="center"
        gap={1}
      >
        <AttendanceCodeCircle className="code" size={CODE_CIRCLE_SIZE} />
        {children}
      </Stack>
    </Tooltip>
  );
};
